import React, { useMemo, useState } from 'react';
import { useRememberedState, useRememberedScroll } from '../hooks/useRemembered';
import {
    CheckSquare, ArrowRight, CheckCircle2, AlertTriangle, AlertCircle, AtSign, MessageSquare,
    List, FileText, CircleDot,
    ChevronRight, Eye, EyeOff, Undo2
} from 'lucide-react';
import { Button } from './Button';
import { supabase } from '../services/supabaseClient';
import { anker } from '../tutorial/anker';
import { istUebungsprojekt } from '../services/uebung/uebungService';
import { useNotifications } from '../hooks/useNotifications';
import type { AppNotification } from '../services/notificationStore';
import { buildTaskGroups, countTasks, selectTaskProjects, startOfToday, type Priority, type TaskProject } from '../services/taskList';
import { heuteLokalIso } from './planungsphase/planungLogic';
import { ergaenzePlanungAufgaben } from './planungsphase/planungAufgaben';
import { ampelEingangFuer, waehlePlanungProjekte } from './dashboard/planungHandlungsbedarf';
import { usePlanungAmpeln } from './dashboard/usePlanungAmpeln';
import type { ListProject } from '../services/listProject';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════
// Aufgabe, Prioritaet und Projektgruppe leben in services/taskList.ts —
// dieselbe Ableitung speist das Dashboard (Kachel und Kurzliste).

interface Props {
    /** Die Projektliste der App — Listenauszuege (listProject.ts). */
    projects: ListProject[];
    userProfile: any;
    /**
     * Bekommt aus den Aufgabengruppen nur den schmalen `TaskProject`
     * (taskList.ts, `ergaenzePlanungAufgaben` reicht keinen Typparameter
     * durch); aus den Erwaehnungen den Listeneintrag selbst.
     */
    onOpenProject: (project: TaskProject) => void;
    onRefresh: () => void;
}

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const PRIORITY_CONFIG: Record<Priority, { label: string; color: string; bg: string; border: string; icon: typeof AlertCircle; dot: string }> = {
    urgent: { label: 'Dringend', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: AlertCircle, dot: 'bg-red-500' },
    soon:   { label: 'Bald fällig', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, dot: 'bg-amber-400' },
    normal: { label: 'Offen', color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', icon: CircleDot, dot: 'bg-slate-300' },
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const TasksView: React.FC<Props> = ({ projects: alleProjekte, userProfile, onOpenProject, onRefresh }) => {
    // ÜBUNGSPROJEKTE FALLEN IN KEINE AUSWERTUNG (docs/lernzentrum-tutorial-plan.md,
    // 1.11 und 8.2). Gefiltert wird hier und nicht beim Laden, weil die Liste als
    // Prop ankommt und dieselbe Liste zugleich die Projektverwaltung speist, die
    // Übungsprojekte sehr wohl zeigt.
    const projects = useMemo(() => alleProjekte.filter(p => !istUebungsprojekt(p)), [alleProjekte]);

    const [activeTab, setActiveTab] = useRememberedState<'tasks' | 'mentions'>('tasks.tab', 'tasks');
    const scrollRef = useRememberedScroll('tasks.scroll');
    const [priorityFilter, setPriorityFilter] = useRememberedState<'all' | Priority>('tasks.priority', 'all');
    const [showDismissed, setShowDismissed] = useRememberedState('tasks.showDismissed', false);
    const [togglingId, setTogglingId] = useState<string | null>(null);

    // ── Toggle NEP (is_optional) ──
    const toggleOptional = async (serviceDbId: string, setOptional: boolean) => {
        if (!serviceDbId) {
            console.error('NEP toggle: no serviceDbId provided');
            return;
        }
        setTogglingId(serviceDbId);
        try {
            console.log(`NEP toggle: setting is_optional=${setOptional} for service ${serviceDbId}`);
            const { data, error } = await supabase
                .from('project_services')
                .update({ is_optional: setOptional })
                .eq('id', serviceDbId)
                .select();
            if (error) {
                console.error('NEP toggle supabase error:', error);
                throw error;
            }
            console.log('NEP toggle success:', data);
            onRefresh();
        } catch (e) {
            console.error('NEP toggle error:', e);
            alert('Fehler beim Aktualisieren: ' + (e as any)?.message || 'Unbekannt');
        } finally {
            setTogglingId(null);
        }
    };

    // ── Planungs-Ampeln (Stufe 1.5): dieselbe Sammelabfrage und dieselbe
    // Zuordnung wie im Dashboard, damit beide Listen dasselbe sagen. Rot macht
    // die Planungsaufgabe dringend und nennt den Grund; ohne Ampel wie bisher.
    const planungProjekte = useMemo(() => waehlePlanungProjekte(projects, userProfile), [projects, userProfile]);
    const planungEingaenge = useMemo(() => planungProjekte.map(ampelEingangFuer), [planungProjekte]);
    const heuteIso = heuteLokalIso();
    const { ampeln: planungAmpeln, weiter: planungWeiter } = usePlanungAmpeln(planungEingaenge, heuteIso);

    // ── Aufgaben (services/taskList.ts, wortgleich mit dem Dashboard) ──
    const taskGroups = useMemo(
        () => ergaenzePlanungAufgaben(buildTaskGroups(selectTaskProjects(projects, userProfile), startOfToday(), userProfile), planungAmpeln, planungWeiter),
        [projects, userProfile, planungAmpeln, planungWeiter],
    );

    // ── Mentions (gemeinsamer Speicher — derselbe Stand wie Glocke und Dashboard) ──
    const { items: notifications, unreadCount, markRead } = useNotifications(userProfile?.id);
    const handleResolveMention = async (notif: AppNotification) => {
        if (!userProfile) return;
        try {
            await markRead(notif);
        } catch (e) {
            console.error(e);
            alert("Fehler beim Speichern.");
        }
    };

    // ── Filtered tasks ──
    const filteredGroups = priorityFilter === 'all'
        ? taskGroups
        : taskGroups
            .map(g => ({ ...g, tasks: g.tasks.filter(t => t.priority === priorityFilter) }))
            .filter(g => g.tasks.length > 0);

    const { total: totalTasks, urgent: totalUrgent, soon: totalSoon, dismissed: totalDismissed } = countTasks(taskGroups);

    // ═══════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════

    return (
        <div ref={scrollRef} data-glass-view="aufgaben" className="h-full overflow-y-auto bg-slate-50 p-4 md:p-6">
            {/* ── HEADER ── */}
            <header className="mb-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                        Aufgaben & Benachrichtigungen
                    </h1>
                    <p className="text-sm text-slate-500 mt-0.5">Offene Dokumente, Pflichten und Erwähnungen</p>
                </div>

                <div data-glass="umschalter" className="flex bg-white rounded-xl shadow-sm border border-slate-200 p-1">
                    <button
                        data-tutorial="aufgaben.reiter-aufgaben"
                        onClick={() => setActiveTab('tasks')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${activeTab === 'tasks' ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        <List className="w-4 h-4" /> Aufgaben
                        {totalUrgent > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{totalUrgent}</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('mentions')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${activeTab === 'mentions' ? 'bg-amber-50 text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        <AtSign className="w-4 h-4" /> Erwähnungen
                        {unreadCount > 0 && <span className="bg-amber-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{unreadCount}</span>}
                    </button>
                </div>
            </header>

            {activeTab === 'tasks' ? (
                <>
                    {/* ── SUMMARY + FILTER ── */}
                    {totalTasks > 0 && (
                        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                            {/* Summary counters */}
                            <div data-glass="kennzahlen" className="flex items-center gap-3">
                                <div className="flex items-center gap-1.5 text-sm">
                                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                    <span className="font-bold text-red-700">{totalUrgent}</span>
                                    <span className="text-slate-400">Dringend</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-sm">
                                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                    <span className="font-bold text-amber-700">{totalSoon}</span>
                                    <span className="text-slate-400">Bald fällig</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-sm">
                                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                    <span className="font-bold text-slate-600">{totalTasks - totalUrgent - totalSoon}</span>
                                    <span className="text-slate-400">Offen</span>
                                </div>
                            </div>

                            {/* Priority filter */}
                            <div className="flex items-center gap-2">
                                <div data-glass="umschalter" className="flex bg-white rounded-lg border border-slate-200 p-0.5">
                                    {([
                                        { k: 'all' as const, l: 'Alle' },
                                        { k: 'urgent' as const, l: 'Dringend', dot: 'bg-red-500' },
                                        { k: 'soon' as const, l: 'Bald fällig', dot: 'bg-amber-400' },
                                        { k: 'normal' as const, l: 'Offen', dot: 'bg-slate-300' },
                                    ]).map(f => (
                                        <button
                                            key={f.k}
                                            {...anker('aufgaben.filter-dringend', f.k === 'urgent')}
                                            onClick={() => setPriorityFilter(f.k)}
                                            className={`px-3 py-1.5 rounded-md text-[11px] font-bold flex items-center gap-1.5 transition-all ${priorityFilter === f.k ? 'bg-slate-100 text-slate-800 shadow-sm' : 'text-slate-400'}`}
                                        >
                                            {f.dot && <span className={`w-2 h-2 rounded-full ${f.dot}`} />}
                                            {f.l}
                                        </button>
                                    ))}
                                </div>
                                {totalDismissed > 0 && (
                                    <button
                                        onClick={() => setShowDismissed(!showDismissed)}
                                        className={`px-3 py-1.5 rounded-lg text-[11px] font-bold flex items-center gap-1.5 border transition-all ${showDismissed ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-white text-slate-400 border-slate-200 hover:text-slate-600'}`}
                                    >
                                        {showDismissed ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                        {totalDismissed} NEP
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── TASK LIST ── */}
                    {filteredGroups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 bg-white rounded-2xl border border-slate-200 text-slate-400">
                            <CheckCircle2 className="w-14 h-14 mb-3 text-emerald-200" />
                            <p className="text-lg font-medium text-slate-600">
                                {totalTasks === 0 ? 'Alles erledigt!' : 'Keine Aufgaben mit diesem Filter'}
                            </p>
                            <p className="text-sm">
                                {totalTasks === 0
                                    ? 'Keine offenen Aufgaben gefunden.'
                                    : 'Ändere den Filter um andere Aufgaben zu sehen.'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredGroups.map((group, groupIndex) => {
                                const borderColor = group.urgentCount > 0
                                    ? 'border-red-200 ring-1 ring-red-100'
                                    : group.soonCount > 0
                                        ? 'border-amber-200'
                                        : 'border-slate-200';

                                return (
                                    <div key={group.project.id} {...anker('aufgaben.projektgruppe', groupIndex === 0)} className={`bg-white rounded-xl border overflow-hidden ${borderColor}`}>
                                        {/* Project header */}
                                        <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 bg-slate-50/50">
                                            <div className="flex items-center gap-3 min-w-0">
                                                {/* Priority indicator */}
                                                <div className={`w-1.5 h-10 rounded-full shrink-0 ${group.urgentCount > 0 ? 'bg-red-500' : group.soonCount > 0 ? 'bg-amber-400' : 'bg-slate-200'}`} />
                                                <div className="min-w-0">
                                                    <h3 className="font-bold text-sm text-slate-800 truncate">{group.projectName}</h3>
                                                    <p className="text-[11px] text-slate-400 flex items-center gap-2">
                                                        <span className="font-mono">#{group.projectNumber}</span>
                                                        <span>·</span>
                                                        <span>{group.coordinator}</span>
                                                        {group.startDate && <>
                                                            <span>·</span>
                                                            <span>Start: {new Date(group.startDate).toLocaleDateString('de-DE')}</span>
                                                        </>}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {group.urgentCount > 0 && (
                                                    <span className="bg-red-100 text-red-700 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-red-200 flex items-center gap-1">
                                                        <AlertCircle className="w-3 h-3" /> {group.urgentCount} dringend
                                                    </span>
                                                )}
                                                <button
                                                    {...anker('aufgaben.projekt-oeffnen', groupIndex === 0)}
                                                    onClick={() => onOpenProject(group.project)}
                                                    className="text-[11px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg border border-blue-100 flex items-center gap-1 transition-colors"
                                                >
                                                    Projekt öffnen <ArrowRight className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Tasks */}
                                        <div className="divide-y divide-slate-50">
                                            {group.tasks.map((task, idx) => {
                                                const cfg = PRIORITY_CONFIG[task.priority];
                                                const Icon = task.type === 'document' ? FileText : AlertTriangle;
                                                const isToggling = togglingId === task.serviceDbId;

                                                return (
                                                    <div
                                                        key={idx}
                                                        {...anker('aufgaben.zeile', groupIndex === 0 && idx === 0)}
                                                        className="flex items-center px-4 py-3 hover:bg-slate-50/80 transition-colors group"
                                                    >
                                                        {/* Priority dot */}
                                                        <div className={`w-2 h-2 rounded-full shrink-0 mr-3 ${cfg.dot}`} />

                                                        {/* Icon */}
                                                        <div className={`p-2 rounded-lg mr-3 shrink-0 ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                                                            <Icon className="w-4 h-4" />
                                                        </div>

                                                        {/* Content — clickable to open project */}
                                                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpenProject(group.project)}>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-bold text-slate-800 truncate">{task.label}</span>
                                                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} border ${cfg.border} shrink-0`}>
                                                                    {cfg.label}
                                                                </span>
                                                            </div>
                                                            {task.detail && (
                                                                <p className="text-[11px] text-slate-400 mt-0.5 truncate">{task.detail}</p>
                                                            )}
                                                        </div>


                                                        {/* NEP dismiss button (only for document tasks with a DB id) */}
                                                        {task.type === 'document' && task.serviceDbId && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); toggleOptional(task.serviceDbId!, true); }}
                                                                disabled={isToggling}
                                                                className="ml-2 px-2 py-1 rounded-lg text-[11px] font-bold text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-200 transition-all shrink-0 flex items-center gap-1 disabled:opacity-50"
                                                                title="Als NEP/Eventualposition markieren (ausblenden)"
                                                            >
                                                                <EyeOff className="w-3 h-3" />
                                                                <span className="hidden sm:inline">NEP</span>
                                                            </button>
                                                        )}

                                                        {/* Arrow */}
                                                        <ChevronRight
                                                            className="w-4 h-4 text-slate-200 group-hover:text-blue-400 shrink-0 ml-1 transition-colors cursor-pointer"
                                                            onClick={() => onOpenProject(group.project)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Dismissed NEP tasks (collapsible) */}
                                        {showDismissed && group.dismissedTasks.length > 0 && (
                                            <div className="border-t border-dashed border-slate-200 bg-slate-50/50">
                                                <div className="px-4 py-2 flex items-center gap-2">
                                                    <EyeOff className="w-3 h-3 text-slate-300" />
                                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">NEP-Positionen (ausgeblendet)</span>
                                                </div>
                                                <div className="divide-y divide-slate-100">
                                                    {group.dismissedTasks.map((task, idx) => {
                                                        const isToggling = togglingId === task.serviceDbId;
                                                        return (
                                                            <div key={idx} className="flex items-center px-4 py-2.5 opacity-50 hover:opacity-80 transition-opacity">
                                                                <div className="w-2 h-2 rounded-full bg-slate-200 shrink-0 mr-3" />
                                                                <div className="p-1.5 rounded-lg mr-3 shrink-0 bg-slate-100 text-slate-400 border border-slate-200">
                                                                    <FileText className="w-3.5 h-3.5" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <span className="text-xs text-slate-500 line-through">{task.label}</span>
                                                                    <span className="text-[10px] text-indigo-400 ml-2 font-bold">NEP</span>
                                                                </div>
                                                                {task.serviceDbId && (
                                                                    <button
                                                                        onClick={() => toggleOptional(task.serviceDbId!, false)}
                                                                        disabled={isToggling}
                                                                        className="ml-2 px-2 py-1 rounded-lg text-[11px] font-bold text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 border border-indigo-200 transition-all shrink-0 flex items-center gap-1 disabled:opacity-50"
                                                                        title="Wieder als Aufgabe anzeigen"
                                                                    >
                                                                        <Undo2 className="w-3 h-3" />
                                                                        <span className="hidden sm:inline">Wiederherstellen</span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            ) : (
                /* ── MENTIONS TAB ── */
                notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 bg-white rounded-2xl border border-slate-200 text-slate-400">
                        <MessageSquare className="w-14 h-14 mb-3 text-blue-200" />
                        <p className="text-lg font-medium text-slate-600">Keine Erwähnungen</p>
                        <p className="text-sm">Alles abgearbeitet.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {notifications.map((notif, idx) => {
                            const isRead = notif.status === 'read';
                            const p = projects.find(proj => proj.id === notif.project_id);

                            return (
                                <div key={idx} className={`bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden ${isRead ? 'border-slate-200' : 'border-amber-200'}`}>
                                    {!isRead && <div className="absolute top-0 left-0 w-1 h-full bg-amber-400" />}
                                    <div className="flex flex-col md:flex-row justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full flex items-center gap-1 ${isRead ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'}`}>
                                                    <AtSign className="w-3 h-3" /> Erwähnung
                                                </span>
                                                <span className="text-[11px] text-slate-400">{new Date(notif.created_at).toLocaleString('de-DE')}</span>
                                                <span className="text-[11px] text-slate-500">von <b>{notif.sender_name || 'Unbekannt'}</b></span>
                                            </div>
                                            <p className={`text-sm leading-relaxed p-3 rounded-lg border ${isRead ? 'text-slate-400 border-slate-100 bg-slate-50/50' : 'text-slate-800 bg-amber-50/30 border-amber-100'}`}>
                                                {notif.content}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end justify-between gap-2 shrink-0">
                                            <h4 className="font-bold text-slate-700 text-sm text-right">{notif.project_name}</h4>
                                            <div className="flex gap-2">
                                                {!isRead ? (
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => handleResolveMention(notif)}
                                                        className="text-xs h-8 bg-green-50 text-green-700 hover:bg-green-100 border-green-200"
                                                        icon={<CheckCircle2 className="w-3 h-3" />}
                                                    >
                                                        Erledigt
                                                    </Button>
                                                ) : (
                                                    <span className="text-xs text-slate-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Gelesen</span>
                                                )}
                                                {p && (
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => onOpenProject(p)}
                                                        className="text-xs h-8"
                                                        icon={<ArrowRight className="w-3 h-3" />}
                                                    >
                                                        Zum Projekt
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            )}
        </div>
    );
};
